// src/oura/onboarding/derive.ts
//
// What an onboarding record MEANS: the steps in order, how many are done,
// and the one thing that has to happen next. Pure functions over the payload,
// so the staff screen, the Businesses list and the merchant page all read the
// same answer.

import type { Tone } from "@oura/ui";
import type { AccessItem, AccessKey, MerchantOnboarding, MessageKind, Onboarding, OnboardingPerson, SentMessage } from "@service/aeo/onboarding";
import { isKnownLoginStatus } from "@service/aeo/onboarding";
import { ENGINE_LABEL } from "../lib/oura";
import { VIS_ENGINES } from "../visibility/model";
import { SCAN_DRAFT_UI_ON, scanDraftMode } from "../newscan/flags";
import { firstCheckHref } from "../newscan/firstCheckLinks";

/**
 * The First AI check opens the draft flow (JOURNEY-SPEC §2.4): the 50 are
 * reviewed before the paid start. Off, step 4 is today's StartScanDrawer and
 * the record's draft is not read.
 */
export const FIRST_CHECK_DRAFT_FLOW: boolean = SCAN_DRAFT_UI_ON && scanDraftMode() !== "absent";

/** A client gets a reminder once they have been quiet this long. */
export const REMIND_AFTER_DAYS = 3;
const DAY = 86_400_000;

/* ------------------------------------------------------------------------- *
 * How long the first AI check takes (#3978)
 * ------------------------------------------------------------------------- */

/**
 * MEASURED, not estimated. The drawer used to promise "about 12 minutes",
 * which no real run has ever matched — staff who are told 12 read a check that
 * is working normally as stuck, and rerun it.
 *
 * Staging run `5b733ecc-8656-4ae5-a4c3-4985efd639ce`, 6 Oct 2026:
 * ChatGPT's 50 questions took 1,051 s (17.5 min), Gemini's 278 s (4.6 min),
 * and Google's AI was still going 14 min later — 38 min in all and not
 * finished when the run was stopped. The staff test doc says 25 to 35.
 *
 * So: 40 minutes, said as "about", and never as a countdown. There is no
 * backend figure to read for this — `OnboardingScan` carries status, percent
 * and timestamps, no estimate — so when that lands, this constant is the one
 * place to replace. (`AeoAssessmentEta` is a REMAINING estimate for a run
 * already going, which is a different question from how long one takes.)
 */
export const FIRST_CHECK_MINUTES = 40;

/** "About 40 minutes." One sentence, used by the drawer, the snackbar and the running step. */
export const FIRST_CHECK_TIME = `About ${FIRST_CHECK_MINUTES} minutes.`;

/**
 * "ChatGPT, Gemini and Google's AI" — the engines the check actually measures,
 * read from the list the Visibility screen reports (`VIS_ENGINES`) and the one
 * place their names live (`ENGINE_LABEL`). The drawer used to name two of the
 * three, which is the other half of #3978; built this way, a fourth engine
 * cannot appear on Visibility while this sentence still says two.
 */
export const FIRST_CHECK_ENGINES = VIS_ENGINES.map((e) => ENGINE_LABEL[e])
    .filter(Boolean)
    .reduce((text, name, i, all) => (i === 0 ? name : i === all.length - 1 ? `${text} and ${name}` : `${text}, ${name}`), "");

/** The access rows. */
const ACCESS_STEPS: StepKey[] = ["gbp", "website", "google"];

/**
 * One setup (`flow: "one_setup"`): the kick-off is the one Setup email. It
 * carries the login and asks for the questions and the access together, so
 * there is no login or access request to send after it.
 */
export function isOneSetup(o: Pick<Onboarding, "flow">): boolean {
    return o.flow === "one_setup";
}

export type StepWho = "staff" | "merchant" | "oura";
/**
 * done:   finished.
 * ours:   staff have to act.
 * oura:   Oura is working on it by itself.
 * theirs: waiting on the client.
 * later:  cannot start yet.
 */
export type StepState = "done" | "ours" | "oura" | "theirs" | "later";
export type StepKey = "paid" | "kickoff" | "form" | "scan" | "baseline" | "login" | "request" | "gbp" | "website" | "google";

export interface OnboardingStep {
    key: StepKey;
    title: string;
    who: StepWho;
    state: StepState;
    /** When it was done. Null until then. */
    at: string | null;
    /** The one fact on the right of the row. */
    fact: string;
    late: boolean;
}

export const STEP_TONE: Record<StepState, Tone> = {
    done: "green",
    ours: "blue",
    oura: "violet",
    theirs: "amber",
    later: "gray",
};

export const ACCESS_LABEL: Record<AccessKey, string> = {
    gbp: "Google Business Profile",
    website: "Website",
    gsc: "Search Console",
    ga4: "Google Analytics",
};

export function isHeld(a: AccessItem): boolean {
    return a.status === "connected" || a.status === "confirmed";
}

/**
 * The access this client is asked for. An Online client has no outlet, so no
 * Google Business Profile: it is never asked for, shown or waited on (O9).
 */
export function accessAsked(o: Pick<Onboarding, "access" | "buyMode">): AccessItem[] {
    return o.buyMode === "online" ? o.access.filter((a) => a.key !== "gbp") : o.access;
}

export function daysSince(iso: string | null, now = Date.now()): number | null {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? null : Math.max(0, Math.floor((now - t) / DAY));
}

/** A length of time: "today", "1 day", "4 days". */
export function daysLabel(n: number): string {
    if (n === 0) return "today";
    return n === 1 ? "1 day" : `${n} days`;
}

/** A point in time: "today", "1 day ago", "4 days ago". */
export function agoLabel(n: number): string {
    return n === 0 ? "today" : `${daysLabel(n)} ago`;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2 Oct 2026": always with the year (23 Oct 2026), the same as dayMonthYear. */
export function dayMonth(iso: string | null): string {
    return dayMonthYear(iso);
}

/** "2 Oct 2026", always with the year. */
export function dayMonthYear(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso.length <= 10 ? `${iso}T00:00:00` : iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${d.getDate()} ${MONTHS[d.getMonth()]} ${d.getFullYear()}`;
}

/** "2 Oct, 10:42". */
export function dayTime(iso: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    return `${dayMonth(iso)}, ${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/**
 * The name to call someone by: the name as it was given.
 *
 * Never cut to a first word. "Siew Ling" is one given name, "Dr Mei Tan" is
 * Dr Tan, and a wrong short name in a client's inbox is worse than a long one.
 */
export function callName(name: string): string {
    return name.trim().replace(/\s+/g, " ");
}

/** "Sample Tea Co." stays as it is, "Aria Skin Clinic" takes a full stop: a name ending a sentence never reads "Co..". */
export function nameStop(name: string): string {
    const n = name.trim();
    return /[.!?]$/.test(n) ? n : `${n}.`;
}

const TITLES = /^(dr|dato'?|datuk|datin|puan|encik|mr|mrs|ms|madam|prof)\.?$/i;

/**
 * The name an email opens with, by the rule the kick-off and login emails
 * already use (BE `kickoff_first_name`, `login_first_name`, and the sample
 * login invite): the first name as it was given, so one client is greeted one
 * way in every email. A title is skipped ("Dr Mei Tan" is Mei), never "Hi Dr,".
 */
export function greetName(name: string): string {
    const words = callName(name).split(" ").filter(Boolean);
    const rest = words.length > 1 && TITLES.test(words[0]) ? words.slice(1) : words;
    return rest[0] ?? "there";
}

/** The newest matching message, by its time, whatever order the list came in. */
/**
 * A kick-off or form reminder sent before the record moved onto one setup
 * (#4454): old-flow history, not the Setup email or a setup reminder. It stays
 * in the Emails list but no step counts it.
 */
export function beforeFlowSwitch(o: Pick<Onboarding, "flowSwitchedAt">, m: SentMessage): boolean {
    if (!o.flowSwitchedAt || (m.kind !== "kickoff" && m.kind !== "form_reminder")) return false;
    return Date.parse(m.sentAt) < Date.parse(o.flowSwitchedAt);
}

export function lastSent(o: Onboarding, ...kinds: Array<SentMessage["kind"]>): SentMessage | null {
    let latest: SentMessage | null = null;
    for (const m of o.sent) {
        if (!kinds.includes(m.kind) || beforeFlowSwitch(o, m)) continue;
        if (!latest || Date.parse(m.sentAt) > Date.parse(latest.sentAt)) latest = m;
    }
    return latest;
}

/** When the client was last written to about this, or null when never. */
function lastTouch(o: Onboarding, ...kinds: MessageKind[]): string | null {
    return lastSent(o, ...kinds)?.sentAt ?? null;
}

function latest(...dates: Array<string | null | undefined>): string | null {
    const times = dates.filter((d): d is string => !!d).sort();
    return times.length > 0 ? times[times.length - 1] : null;
}

/** The last time anything moved on access: a message from us, or an access from them. */
function lastAccessMove(o: Onboarding): string | null {
    return latest(
        lastTouch(o, "access_reminder", "access_request", ...(isOneSetup(o) ? (["kickoff"] as const) : [])),
        ...o.access.flatMap((a) => [a.since, a.claimedAt]),
    );
}

/** Who the access emails go to: the person the form named, or the main contact when it gave no email. */
export function accessRecipient(o: Onboarding): OnboardingPerson {
    const named = o.accessContact;
    return named?.email ? { name: named.name, email: named.email } : o.contact;
}

/**
 * Whether two records name the same person, for the purpose of WRITING ABOUT
 * THEM.
 *
 * Either mark of identity is enough, and that is deliberate.
 *
 * The email catches one person spelled two ways. A form answer typed by the
 * client arrives as "Jovi Chu Marketing Manager +60163190603" while the record
 * staff typed says "Jovi Chu" — one inbox, two strings — and comparing the
 * strings made the Next card read "Needs Jovi Chu Marketing Manager
 * +60163190603 and Jovi Chu" (#4008).
 *
 * The name catches the other half: one person under two addresses, a work
 * address as the contact and a personal one for the login. Matching on the
 * email alone left that reading "Needs Jovi Chu and Jovi Chu" (#4025 FE-2).
 *
 * Two different people who really do share a name therefore read as one here.
 * That is the right way to be wrong: this decides how many names a sentence
 * prints, and a sentence that says one name twice is wrong every time, while
 * two genuine namesakes at one small business is rare and still correct to
 * address once. Nothing here picks a recipient or an address — the emails are
 * read from the record itself (`accessRecipient`, `o.login.contact`), so
 * collapsing two namesakes in a sentence can never send a message to the
 * wrong inbox.
 */
function samePerson(a: OnboardingPerson, b: OnboardingPerson): boolean {
    const mail = (p: OnboardingPerson) => p.email.trim().toLowerCase();
    const name = (p: OnboardingPerson) => callName(p.name).toLowerCase();
    if (name(a) && name(a) === name(b)) return true;
    return !!mail(a) && mail(a) === mail(b);
}

/**
 * The name to show for the person who looks after access.
 *
 * When their email is the main contact's, the contact record is the name to
 * use: it was typed here and staff can correct it, while the access name came
 * out of the client's own free-text form answer and may carry a job title and
 * a phone number with it (#4008). A genuinely different person keeps their own
 * name, however it was typed — there is nothing better to show.
 */
export function accessPersonName(o: Onboarding): string | null {
    if (!o.accessContact) return null;
    const named = { name: o.accessContact.name, email: o.accessContact.email ?? "" };
    return callName(samePerson(named, o.contact) ? o.contact.name : named.name);
}

/**
 * Who still has something to do for access.
 *
 * The invites are the access contact's. Search Console and Analytics are
 * connected from the client's own page, so once only those are left the wait
 * is on whoever holds the login.
 */
export function accessOwner(o: Onboarding): OnboardingPerson {
    const invitesLeft = accessAsked(o).some((a) => (a.key === "gbp" || a.key === "website") && a.status === "not_started");
    return invitesLeft ? accessRecipient(o) : o.login.contact ?? o.contact;
}

/** The access rows on the Steps list. Search Console and Analytics are 1 row. */
export type AccessRowKey = "gbp" | "website" | "google";

export function accessRowOf(key: AccessKey): AccessRowKey {
    return key === "gsc" || key === "ga4" ? "google" : key;
}

/**
 * An Online client (O9): its first check waits for brand checks, and its
 * login and access request go out once the form is in, before any baseline.
 * Only while no first check has run: one that ran as a local check keeps its
 * baseline and walks the usual order.
 */
export function onlineBeforeScan(o: Onboarding): boolean {
    return o.buyMode === "online" && o.scan.status === "not_started";
}

/** The First AI check's draft, read only while the draft flow is on and the check has not started. */
function firstCheckDraft(o: Onboarding, draftFlow: boolean) {
    if (!draftFlow || o.scan.status !== "not_started" || !o.form.answeredAt) return null;
    const d = o.scan.draft;
    return d && d.status !== "discarded" && d.status !== "superseded" ? d : null;
}

/**
 * The First AI check's draft flow (JOURNEY-SPEC §2.5): at Review, or at Start
 * when the 50 are approved, nothing is left to decide, or a failed check runs
 * again on the same approved 50.
 */
export function firstCheckDraftHref(businessId: string, at: "review" | "start" = "review", draftId?: string | null): string {
    return firstCheckHref(businessId, { draftId, step: at === "start" ? "start" : null });
}

/** Where the draft flow opens for this record: Start once there is nothing left to review. */
export function firstCheckOpensAt(o: Onboarding): "review" | "start" {
    if (o.scan.status === "failed") return "start";
    const d = o.scan.draft;
    if (!d) return "review";
    return d.status === "approved" || (d.status === "ready" && d.openDecisions === 0) ? "start" : "review";
}

/** "1 needs your call", "22 need your call": the step row's short form. */
const toDecide = (k: number) => `${k} ${k === 1 ? "needs" : "need"} your call`;

/**
 * The kick-off waits for a payment (call 18): with the joined journey on (its
 * payment route ships with it), while nothing is paid and no kick-off went
 * out. A record whose kick-off was sent before any payment was recorded reads
 * as today's flow.
 */
function heldForPayment(o: Onboarding, draftFlow: boolean): boolean {
    return draftFlow && !o.paidAt && !lastSent(o, "kickoff");
}

export function onboardingSteps(o: Onboarding, now = Date.now(), draftFlow = FIRST_CHECK_DRAFT_FLOW): OnboardingStep[] {
    const kickoff = lastSent(o, "kickoff");
    const oneSetup = isOneSetup(o);
    // A one-setup client is asked for access in the Setup email itself.
    const request = oneSetup ? kickoff : lastSent(o, "access_request");
    // The wait is counted from the first ask. A reminder does not make a late form on time.
    const formWait = daysSince(kickoff?.sentAt ?? null, now);
    const accessWait = daysSince(request?.sentAt ?? null, now);
    const scanDone = o.scan.status === "complete";
    const online = onlineBeforeScan(o);
    // The login and the access request: after the AI check, or for an Online client after the form (O9).
    const handoverOpen = scanDone || (online && !!o.form.answeredAt);
    // What the login and the access request wait for (JOURNEY-SPEC §2.4a): an Online client's wait for the form.
    const handoverAfter = online ? "After the form" : "After the AI check";
    const draft = firstCheckDraft(o, draftFlow);
    // Access opens once the client has been asked for it.
    const open = !!request;
    // A reminder answers a late form for as long as the reminder is fresh.
    const formQuiet = daysSince(lastTouch(o, "form_reminder", "kickoff"), now);

    const access = (key: AccessKey) => o.access.find((a) => a.key === key);
    const waitingFact = accessWait !== null ? `Asked ${agoLabel(accessWait)}` : "Not asked yet";
    const accessLate = accessWait !== null && accessWait >= REMIND_AFTER_DAYS;

    const inviteStep = (key: "gbp" | "website", title: string): OnboardingStep => {
        const a = access(key);
        const held = !!a && isHeld(a);
        const claimed = a?.status === "claimed";
        return {
            key, title, who: claimed ? "staff" : "merchant",
            state: held ? "done" : claimed ? "ours" : open ? "theirs" : "later",
            at: held ? a!.since : null,
            fact: held ? `Since ${dayMonth(a!.since)}`
                : claimed ? `${callName(a!.claimedBy ?? o.contact.name)} says done`
                : open ? waitingFact : oneSetup ? "After the Setup email" : "After the request",
            late: !held && !claimed && open && accessLate,
        };
    };
    const gsc = access("gsc");
    const ga4 = access("ga4");
    const googleHeld = [gsc, ga4].filter((a) => a && isHeld(a)).length;
    const payHeld = heldForPayment(o, draftFlow);

    const steps: OnboardingStep[] = [
        {
            // The client's to pay, as the Next card's "Waiting on {Business}" says; recording it is ours.
            key: "paid", title: "Payment received", who: o.paidAt ? "staff" : "merchant",
            state: o.paidAt ? "done" : "theirs", at: o.paidAt,
            fact: o.paidAt ? dayMonth(o.paidAt) : "Not paid yet", late: false,
        },
        {
            key: "kickoff", title: oneSetup ? "Setup email" : "Kick-off email", who: "staff",
            // Held until the payment is recorded (call 18).
            // Answers already in: there is nothing left to send (#4779). A kick-off sent before
            // the switch to one setup, or a client set up before Oura, has none counted here.
            state: kickoff || o.form.answeredAt ? "done" : payHeld ? "later" : "ours", at: kickoff?.sentAt ?? null,
            fact: kickoff ? `Sent ${dayMonth(kickoff.sentAt)}` : o.form.answeredAt ? "Answers already in" : payHeld ? "After the payment" : "Not sent", late: false,
        },
        {
            key: "form", title: oneSetup ? "Business details" : "Form answered", who: "merchant",
            state: o.form.answeredAt ? "done" : kickoff ? "theirs" : "later", at: o.form.answeredAt,
            fact: o.form.answeredAt ? dayMonth(o.form.answeredAt)
                : formWait !== null ? (formWait === 0 ? "Not answered yet" : `Waiting ${daysLabel(formWait)}`) : oneSetup ? "After the Setup email" : "After the kick-off",
            late: !o.form.answeredAt && formQuiet !== null && formQuiet >= REMIND_AFTER_DAYS,
        },
        scanStep(o, draft, online),
        // Results and plan, sent together by hand after the check: only where the record tracks it.
        ...(o.resultsSentAt !== undefined ? [{
            key: "baseline" as const, title: "Results and plan", who: "staff" as const,
            // Fact under: sent, or what is left (the timeline), or after the check.
            state: (o.resultsSentAt ? "done" : scanDone ? "ours" : "later") as StepState, at: o.resultsSentAt ?? null,
            fact: o.resultsSentAt ? (o.resultsResentAt ? `Sent again ${dayMonth(o.resultsResentAt)}` : `Sent ${dayMonth(o.resultsSentAt)}`) : scanDone ? "Not sent" : "After the check", late: false,
        }] : []),
        {
            key: "login", title: "Login", who: "staff",
            // A SEND THAT FAILED IS NOT DONE. The old test was `!== "not_sent"`,
            // so `send_failed` (ourteam-backend#5411 BE-1) counted as done and
            // the fact under it still read "Ready to send" -- a step marked
            // finished, describing itself as unstarted, about a client who had
            // been told nothing (#4025 FE-3). It is ours again, and late,
            // because nothing moves until it goes.
            // SENDING IS IN PROGRESS, NOT DONE AND NOT LATE (ourteam-backend#5523):
            // the email is on its way for a few seconds, and offers no send. A
            // status this screen does not know is not done either, and is still
            // ours to look at -- but it offers no send (see nextAction).
            state: o.login.status === "sent" || o.login.status === "signed_in" ? "done"
                : o.login.status === "send_failed" || o.login.status === "sending" ? "ours"
                : handoverOpen ? "ours" : "later",
            at: o.login.sentAt,
            fact: o.login.status === "signed_in" ? `Signed in ${dayMonth(o.login.signedInAt)}`
                : o.login.status === "sent" ? `Sent ${dayMonth(o.login.sentAt)}, not used yet`
                : o.login.status === "send_failed" ? "The email did not go"
                : o.login.status === "sending" ? "Sending the login email…"
                : !isKnownLoginStatus(o.login.status) ? "Status not recognised"
                : handoverOpen ? "Ready to send" : handoverAfter,
            late: o.login.status === "send_failed",
        },
        {
            key: "request", title: "Access request", who: "staff",
            state: request ? "done" : handoverOpen ? "ours" : "later", at: request?.sentAt ?? null,
            fact: request ? `Sent ${dayMonth(request.sentAt)}` : handoverOpen ? "Ready to send" : handoverAfter, late: false,
        },
        // An Online client has no Google Business Profile to give (O9): no step for it.
        ...(o.buyMode === "online" ? [] : [inviteStep("gbp", ACCESS_LABEL.gbp)]),
        inviteStep("website", ACCESS_LABEL.website),
        {
            key: "google", title: "Search Console and Analytics", who: "merchant",
            state: googleHeld === 2 ? "done" : open ? "theirs" : "later",
            at: googleHeld === 2 ? latest(ga4?.since, gsc?.since) : null,
            fact: googleHeld === 2 ? `Since ${dayMonth(latest(ga4?.since, gsc?.since))}`
                : open ? `${googleHeld} of 2 connected` : oneSetup ? "After the Setup email" : "After the request",
            late: googleHeld < 2 && open && accessLate,
        },
    ];
    if (!oneSetup) return steps;
    // One setup: the questions and the access are answered together on the
    // merchant's Setup page. The First AI check comes after them but needs only
    // the answers: it can start while access is still coming in, so the
    // baseline is never held up by a slow Google invite. No login or access
    // request rows: the Setup email carried both.
    return steps
        .filter((s) => s.key !== "login" && s.key !== "request")
        // The merchant's own order: Search Console comes second on their Setup page.
        .sort((a, b) => oneSetupOrder(a.key) - oneSetupOrder(b.key));
}

/** The order of steps in a one-setup client's list, matching their Setup page. */
const ONE_SETUP_ORDER: StepKey[] = ["paid", "kickoff", "form", "google", "gbp", "website", "scan", "baseline"];

/** A step missing from the order sorts last, never first. */
const oneSetupOrder = (key: StepKey): number => {
    const index = ONE_SETUP_ORDER.indexOf(key);
    return index === -1 ? Number.MAX_SAFE_INTEGER : index;
};

/** Step 4, First AI check: first match wins (JOURNEY-SPEC §2.4). */
function scanStep(o: Onboarding, draft: ReturnType<typeof firstCheckDraft>, online: boolean): OnboardingStep {
    const step = (state: StepState, fact: string, late = false): OnboardingStep => ({
        key: "scan", title: "First AI check", who: "oura", state, at: o.scan.finishedAt, fact, late,
    });
    if (o.scan.status === "complete") return step("done", dayMonth(o.scan.finishedAt));
    if (o.scan.status === "running") return step("oura", o.scan.percent !== null ? `${o.scan.percent}%` : "Running");
    if (o.scan.status === "failed") return step("ours", "Failed", true);
    if (!o.form.answeredAt) return step("later", "After the answers");
    if (online) return step("later", "Waiting for brand checks");
    if (draft?.status === "building") return step("oura", "Drafting the 50");
    if (draft?.status === "failed") return step("ours", "The draft stopped", true);
    if (draft?.status === "ready" && draft.openDecisions > 0) return step("ours", toDecide(draft.openDecisions));
    return step("ours", "Ready to start");
}

/**
 * The Steps card's rows: the few milestones, not every step (Jack, 20 Oct
 * 2026). The detail is where it is acted on: the Next card, the drawers and
 * the access list beside it.
 */
export type MilestoneKey = "paid" | "kickoff" | "setup" | "scan" | "release" | "baseline";

export interface Milestone {
    key: MilestoneKey;
    title: string;
    state: StepState;
    /** The one fact on the right of the row. */
    fact: string;
    late: boolean;
}

/**
 * Setup's count on staff's card, said so it agrees with the client's. The
 * client counts a part done once THEY have done it; staff once we have
 * checked it, so the same day read "3 of 4" there and "2 of 4" here (audit,
 * 24 Oct 2026). The gap is ours to check, so it is said: "2 of 4 done, 1 to check".
 */
export function setupFact(done: number, total: number, toCheck: number): string {
    return toCheck > 0 ? `${done} of ${total} done, ${toCheck} to check` : `${done} of ${total} done`;
}

/**
 * Payment, the first email, the client's setup (the answers and every
 * access, as one row), the First AI check, and the results and plan. A
 * client from before one setup also has the login and the access request,
 * which are still ours to send after the check.
 */
export function onboardingMilestones(o: Onboarding, now = Date.now(), draftFlow = FIRST_CHECK_DRAFT_FLOW): Milestone[] {
    const steps = onboardingSteps(o, now, draftFlow);
    const of = (key: StepKey) => steps.find((s) => s.key === key);
    const row = (key: MilestoneKey, s: OnboardingStep, title = s.title, fact = s.fact): Milestone => ({ key, title, state: s.state, fact, late: s.late });

    const parts = steps.filter((s) => s.key === "form" || ACCESS_STEPS.includes(s.key));
    const finished = parts.filter((s) => s.state === "done");
    const setup: Milestone = {
        key: "setup",
        title: "Setup",
        // An invite they say is done is ours to confirm, and that comes first even while
        // they still owe another: this card is staff's, and it says when we can act.
        // Otherwise theirs while anything is open.
        state: finished.length === parts.length ? "done"
            : parts.some((s) => s.state === "ours") ? "ours"
            : parts.some((s) => s.state === "theirs") ? "theirs" : "later",
        fact: finished.length === parts.length ? dayMonth(latest(...finished.map((s) => s.at))) || "Done"
            : finished.length === 0 && parts.every((s) => s.state === "later") ? of("form")!.fact
            : setupFact(finished.length, parts.length, parts.filter((s) => s.state === "ours").length),
        late: parts.some((s) => s.late),
    };

    // The check in plain words: Preparing, then the server's percent while it
    // runs (the same figure as the Next card's bar, #4433), then done on the
    // day it finished. This card is staff's: a finished check is done even
    // while its results wait for us, and Results and plan carries that wait
    // (#4436). The merchant's page keeps its own "In progress" until we send.
    const scan = of("scan")!;
    const scanFact = o.scan.status === "running" ? (o.scan.percent !== null ? `${o.scan.percent}%` : "In progress")
        : scan.state === "done" || scan.state === "later" || o.scan.status === "failed" ? scan.fact
        : "Preparing";

    const login = of("login");
    const request = of("request");
    const release: Milestone[] = login && request ? [{
        key: "release",
        title: "Login and access request",
        state: login.state === "done" && request.state === "done" ? "done"
            : login.state === "ours" || request.state === "ours" ? "ours" : "later",
        fact: login.state === "done" && request.state === "done" ? `Sent ${dayMonth(latest(login.at, request.at))}`
            : login.late ? login.fact
            : login.state === "done" || request.state === "done" ? "1 of 2 sent"
            : login.fact,
        late: login.late,
    }] : [];

    const baseline = of("baseline");
    return [
        row("paid", of("paid")!),
        row("kickoff", of("kickoff")!),
        setup,
        row("scan", scan, scan.title, scanFact),
        ...release,
        ...(baseline ? [row("baseline", baseline)] : []),
    ];
}

export function onboardingProgress(o: Onboarding): { done: number; total: number } {
    const rows = onboardingMilestones(o);
    return { done: rows.filter((s) => s.state === "done").length, total: rows.length };
}

export type NextKind =
    | "record_payment" | "send_kickoff" | "wait_form" | "nudge_form"
    | "draft_building" | "draft_failed" | "review_draft" | "start_scan" | "wait_scan" | "retry_scan"
    | "release" | "resend_login" | "wait_login" | "choose_signature" | "confirm_access" | "wait_brand"
    | "wait_access" | "nudge_access" | "send_results" | "publish" | "done";

export interface NextAction {
    kind: NextKind;
    who: StepWho;
    /** The person being waited on, when it is the client's side. */
    waitingOn: string | null;
    /** A tag other than the one `who` gives: "Waiting on {Business}" for a payment. */
    tag: { label: string; tone: Tone } | null;
    title: string;
    why: string;
    /** The button. Null when nobody on our side has anything to press. */
    cta: string | null;
    /** A second, quieter choice beside the button ("Choose another"). */
    secondary: string | null;
    /** True when the button is the expected move, false when it is optional. */
    primary: boolean;
    late: boolean;
    /** The access row a confirmation is about. */
    row: AccessRowKey | null;
}

/**
 * What the plan holds, read beside the record once the first check is done:
 * the signature pair (O4) and whether the schedule was ever published. Absent,
 * the Next card says nothing about either, as before.
 */
export interface OnboardingPlanFacts {
    /** `held`: the pair is on the plan. `needs_person`: Oura could not pick one. `pending`: not known yet. */
    signature: "held" | "needs_person" | "pending";
    /** The question closest to a win, when Oura could not pick: "Q14", and why. */
    closest: { n: number; reason: string } | null;
    /** A dated schedule was ever published. */
    published: boolean;
    /** Nothing is missing to publish it: the Timeline's own yes, and something scheduled. */
    canPublish: boolean;
    /** What the Timeline says is missing, in staff words, when it cannot be published. Absent: not read. */
    blockers?: string[];
}

export interface NextOptions {
    /** The draft flow is on: step 4 reads the record's draft. Default: the flag. */
    draftFlow?: boolean;
    plan?: OnboardingPlanFacts | null;
}

/**
 * The one thing that has to happen next, and who has to do it.
 *
 * Order when two are due, first match wins (JOURNEY-SPEC §2.4): not paid,
 * kick-off, form, draft building or failed, first check review, start,
 * running or failed, login email failed, release, signature, confirm an
 * invite, access late, brand checks (Online), access waiting, publish, done.
 */
export function nextAction(o: Onboarding, now = Date.now(), opts: NextOptions = {}): NextAction {
    const draftFlow = opts.draftFlow ?? FIRST_CHECK_DRAFT_FLOW;
    const plan = opts.plan ?? null;
    const base = { waitingOn: null, row: null, primary: false, late: false, cta: null, secondary: null, tag: null } as const;
    const contact = callName(o.contact.name);
    const business = o.businessName || o.domain;
    const kickoff = lastSent(o, "kickoff");
    if (heldForPayment(o, draftFlow)) {
        // Signed, not paid: the kick-off is held until the payment clears (call 18).
        return {
            ...base, kind: "record_payment", who: "merchant", tag: { label: `Waiting on ${business}`, tone: "gray" },
            title: `Record ${business}'s payment`,
            // Says what the press does and does not do: the kick-off is its own step after it (call 18).
            why: isOneSetup(o)
                ? `Signed ${o.plan?.ref ?? "the quote"} on ${dayMonth(o.createdAt)}. The Setup email is sent next, from this card.`
                : `Signed ${o.plan?.ref ?? "the quote"} on ${dayMonth(o.createdAt)}. The plan starts on the payment date. The kick-off is sent next, from this card.`,
            cta: "Record the payment",
        };
    }
    const oneSetup = isOneSetup(o);
    // The kick-off asks for the answers: once they are in, it is never the next thing to send,
    // whether it went before the switch to one setup or the client was set up before Oura (#4779).
    if (!kickoff && !o.form.answeredAt) {
        return { ...base, kind: "send_kickoff", who: "staff", title: oneSetup ? "Send the Setup email" : "Send the kick-off email", why: `Created ${dayMonth(o.createdAt)}. Nothing sent yet.`, cta: "Review and send", primary: true };
    }
    // Only an invite is confirmed by staff. Search Console and Analytics say "added us" and are connected, never confirmed (#4513).
    const claimed = accessAsked(o).find((a) => a.status === "claimed" && (a.key === "gbp" || a.key === "website"));
    const confirmAccess = (a: NonNullable<typeof claimed>): NextAction => ({
        ...base, kind: "confirm_access", who: "staff", row: accessRowOf(a.key), title: `Confirm the ${a.key === "website" ? "website" : ACCESS_LABEL[a.key]} invite`, why: `${callName(a.claimedBy ?? o.contact.name)} says done.`, cta: "Review the invite", primary: true,
    });
    // One setup: the merchant gives access in any order, often before the
    // answers, so an invite they say is done is ours to confirm now, not
    // hidden behind a wait on their questions.
    // Before the answers are in, an invite to confirm is the useful thing to
    // do. After, the first AI check's work comes first: it is what the
    // merchant waits on. The invite is confirmed further down.
    if (oneSetup && claimed && !o.form.answeredAt) return confirmAccess(claimed);
    if (!o.form.answeredAt && kickoff) {
        const waited = daysSince(kickoff.sentAt, now) ?? 0;
        const reminder = lastSent(o, "form_reminder");
        const quiet = daysSince(reminder?.sentAt ?? kickoff.sentAt, now) ?? 0;
        if (quiet >= REMIND_AFTER_DAYS) {
            // The row under it says how long. This says since when.
            return {
                ...base, kind: "nudge_form", who: "staff", title: oneSetup ? `Remind ${contact} about setup` : `Remind ${contact} about the form`,
                why: `${oneSetup ? "Setup email" : "Kick-off"} sent ${dayMonth(kickoff.sentAt)}. No answer.`, cta: "Review and send", primary: true, late: true,
            };
        }
        const why = reminder
            ? `Reminded ${agoLabel(quiet)}. Waiting ${daysLabel(waited)}.`
            : `Sent ${agoLabel(waited)}.`;
        // Nobody is reminded on the day they were asked.
        const waitTitle = oneSetup ? `Waiting for ${contact} to answer the setup questions` : `Waiting for ${contact} to answer the form`;
        return { ...base, kind: "wait_form", who: "merchant", waitingOn: contact, title: waitTitle, why, cta: quiet === 0 ? null : "Send a reminder" };
    }
    // The answers are in. In one setup too, the First AI check's work comes
    // before any wait on access still missing: the check needs only the
    // answers, and a slow Google invite must not hold up the baseline.
    // Access the merchant says is done was already put first, above.
    if (o.scan.status === "failed") {
        return {
            ...base, kind: "retry_scan", who: "staff", title: "The first AI check failed", why: o.scan.failure ?? "It stopped before it finished.",
            cta: draftFlow ? "Run it again" : "Review and run again", primary: true, late: true,
        };
    }
    const online = onlineBeforeScan(o);
    const answered = dayMonth(o.form.answeredAt);
    // The first AI check starts on our go, never by itself (YW, 30 Sep 2026):
    // the answers are read first, because the check runs on them.
    if (o.scan.status === "not_started" && !online) {
        const draft = firstCheckDraft(o, draftFlow);
        if (draft?.status === "building") {
            // A waiting title: the card never shows a verb without a button.
            const from = draft.fromPitch ? `the pitched 50 and ${contact}'s answers` : `the site, the quote and ${contact}'s answers`;
            return { ...base, kind: "draft_building", who: "oura", title: "Oura is drafting the first AI check", why: `From ${from}. About a minute.` };
        }
        if (draft?.status === "failed") {
            return { ...base, kind: "draft_failed", who: "staff", title: "The first 50 could not be drafted", why: "The draft stopped before it finished.", cta: "Try again", primary: true, late: true };
        }
        if (draft?.status === "ready" && draft.openDecisions > 0) {
            return { ...base, kind: "review_draft", who: "staff", title: "Start the first AI check", why: `Answered ${answered}. ${draft.openDecisions} of 50 questions ${draft.openDecisions === 1 ? "needs" : "need"} your call.${draft.fromPitch ? " The rest matches the pitch." : ""}`, cta: "Review the 50", primary: true };
        }
        const approved = draft?.status === "approved" || draft?.status === "started";
        const why = approved
            ? `50 approved${draft?.approvedBy ? ` by ${callName(draft.approvedBy)}` : ""}, ${dayMonth(draft?.approvedAt ?? null) || "today"}.`
            : draft ? `Answered ${answered}. ${draft.fromPitch ? "Same 50 as the pitch, nothing to decide." : "Nothing left to decide."}`
            : `Answered ${answered}. Check the services and areas.`;
        return { ...base, kind: "start_scan", who: "staff", title: "Start the first AI check", why, cta: "Review and start", primary: true };
    }
    if (o.scan.status === "running") {
        // The percent is the server's; the total is the measured one. Neither
        // is presented as a countdown — a run that overruns the figure is
        // normal, and a number that reads as a promise is what caused #3978.
        const inAll = `About ${FIRST_CHECK_MINUTES} minutes in all.`;
        return { ...base, kind: "wait_scan", who: "oura", title: "The first AI check is running", why: o.scan.percent !== null ? `${o.scan.percent}% done. ${inAll}` : inAll };
    }
    // A login whose email FAILED is the next thing to do, before anything
    // waiting on the client: they cannot sign in, and until BE-1 the record
    // said "sent" so nothing on any screen said so (#4025 FE-3). Its own
    // kind, not `release`: the fix is to send that one email again, and the
    // release flow would review and send the access request with it.
    if (o.login.status === "send_failed") {
        return {
            ...base,
            kind: "resend_login",
            who: "staff",
            title: "The login email did not go",
            why: o.login.failureDetail?.trim() || "The client has not been sent their login, so they cannot sign in.",
            cta: "Send it again",
            primary: true,
            late: true,
        };
    }
    // The login email is on its way (ourteam-backend#5523): nothing to send
    // from here. The release would send the access request with it, so no
    // release is offered either. The screen reads the record again until it
    // reads `sent` or `send_failed` (OnboardingTab's poll).
    if (o.login.status === "sending") {
        return {
            ...base,
            kind: "wait_login",
            who: "oura",
            title: "The login email is on its way",
            why: "It goes out in a few seconds. This screen checks again until it does.",
        };
    }
    // One setup: the Setup email carried the login and the access ask, so there is nothing to release.
    // A status this screen does not know offers no release either: a send only goes out for a known state.
    const request = oneSetup ? kickoff : lastSent(o, "access_request");
    if (!oneSetup && isKnownLoginStatus(o.login.status) && (o.login.status === "not_sent" || !request)) {
        const both = o.login.status === "not_sent" && !request;
        const title = both ? "Send the login and the access request" : o.login.status === "not_sent" ? "Send the login" : "Send the access request";
        // Who it goes to is the card's to show, read from the drawer's own prefill (F10).
        const why = online
            ? `${business} sells online. Its first AI check waits for brand checks; the login and Search Console need not.`
            : both ? `First AI check finished ${dayTime(o.scan.finishedAt)}.`
            : o.login.status === "not_sent" ? `Access request sent ${dayMonth(request?.sentAt ?? null)}. No login yet.`
            : `Login sent ${dayMonth(o.login.sentAt)}. The access request is not sent.`;
        return { ...base, kind: "release", who: "staff", title, why, cta: "Review and send", primary: true };
    }
    // Signature: after the release, which unblocks the client; before publish, which needs it.
    if (plan?.signature === "needs_person" && o.scan.status === "complete") {
        const closest = plan.closest;
        return {
            ...base, kind: "choose_signature", who: "staff", title: `Choose the question ${business} already wins`,
            why: closest ? `No AI answer names ${business} yet. Q${closest.n} is the closest: ${closest.reason}` : `No AI answer names ${business} yet.`,
            cta: closest ? `Use Q${closest.n}` : "Choose a question", secondary: closest ? "Choose another" : null, primary: true,
        };
    }
    // Results and plan (Jack, 20 Oct 2026), in order: staff approve the
    // first check's results; Oura then makes the Baseline Snapshot from them;
    // staff upload the execution timeline, which publishes Oura's schedule; one email
    // sends both. The merchant sees the results only when it goes. Only where
    // the record tracks it.
    if (o.scan.status === "complete" && o.resultsSentAt === null) {
        const approved = Boolean(o.resultsApprovedAt);
        const snapshot = Boolean(o.snapshotReadyAt);
        const uploaded = Boolean(o.resultsPack?.timelineAt);
        const why = !approved
            ? `Finished ${dayTime(o.scan.finishedAt)}. Read the answers, then approve. The client sees nothing until you send.`
            : !snapshot
              ? uploaded ? "Results approved and the timeline is in. Oura is making the Baseline Snapshot; Send opens when it is ready." : "Results approved. Oura is making the Baseline Snapshot, usually a few minutes. Upload the execution timeline meanwhile."
              : !uploaded
                ? "The Baseline Snapshot is ready. Upload the execution timeline, then send both in one email."
                : `The snapshot is ready and the timeline is in. One email sends both to ${contact}.`;
        return { ...base, kind: "send_results", who: approved && !snapshot ? "oura" : "staff", title: approved ? `Send ${business}'s results and plan` : `Approve ${business}'s first AI check`, why, primary: true };
    }
    if (claimed) return confirmAccess(claimed);
    // The schedule follows the baseline, never a slow invite: access steps are
    // dated work like any other, so publishing does not wait for them.
    if (oneSetup && plan && o.scan.status === "complete" && !plan.published && o.resultsSentAt === undefined) return publishAction(o, plan, base, business);
    const steps = onboardingSteps(o, now, draftFlow).filter((s) => ACCESS_STEPS.includes(s.key));
    const held = steps.filter((s) => s.state === "done").length;
    if (held < steps.length) {
        // Invites wait on whoever looks after access; Google on whoever holds the login.
        const invitesLeft = accessAsked(o).some((a) => (a.key === "gbp" || a.key === "website") && a.status === "not_started");
        const googleLeft = accessAsked(o).some((a) => (a.key === "gsc" || a.key === "ga4") && a.status === "not_started");
        // The people, not their names: one person owing both halves must be
        // named once, however differently the two records spell them.
        const people: OnboardingPerson[] = [];
        for (const p of [
            ...(invitesLeft ? [accessRecipient(o)] : []),
            ...(googleLeft ? [o.login.contact ?? o.contact] : []),
        ]) {
            if (!people.some((had) => samePerson(had, p))) people.push(p);
        }
        // When one person holds both, the name staff can correct is the one to
        // use: the contact record was typed here, the access name came out of
        // the client's own free-text answer.
        const nameOf = (p: OnboardingPerson) => callName(samePerson(p, o.contact) ? o.contact.name : p.name);
        const owners = people.map(nameOf);
        const asked = owners.length > 0 ? owners.join(" and ") : nameOf(accessOwner(o));
        const reminder = lastSent(o, "access_reminder");
        const quiet = daysSince(lastAccessMove(o), now) ?? 0;
        // Who owes what, when more than 1 person does.
        const count = owners.length > 1
            ? `${owners[0]}: the invites. ${owners[1]}: Search Console and Analytics.`
            : `${held} of ${steps.length} held.`;
        if (quiet >= REMIND_AFTER_DAYS) {
            return { ...base, kind: "nudge_access", who: "staff", title: `Remind ${asked} about access`, why: `${count} Nothing new for ${daysLabel(quiet)}.`, cta: "Review and send", primary: true, late: true };
        }
        if (online) return waitBrand(base);
        // A client set up before Oura can have no ask on record at all (#4779): say so, and
        // offer the send at once rather than "Asked today" with nothing to press.
        const ask = reminder ?? request ?? kickoff;
        const sinceAsk = daysSince(ask?.sentAt ?? null, now) ?? 0;
        const timing = ask ? `${reminder ? "Reminded" : "Asked"} ${agoLabel(sinceAsk)}.` : "No access request is recorded.";
        return { ...base, kind: "wait_access", who: "merchant", waitingOn: asked, title: `Waiting for ${asked} to give access`, why: `${count} ${timing}`, cta: ask && sinceAsk === 0 ? null : "Send a reminder" };
    }
    if (online) return waitBrand(base);
    if (plan && o.scan.status === "complete" && !plan.published && !o.resultsSentAt) return publishAction(o, plan, base, business);
    return { ...base, kind: "done", who: "oura", title: "Onboarding is complete", why: `All access held since ${dayMonth(o.completedAt)}.` };
}

/** Publish the schedule: the same readiness the Timeline's publish button reads, never "held" while the Timeline says missing. */
function publishAction(o: Onboarding, plan: NonNullable<NextOptions["plan"]>, base: Partial<NextAction>, business: string): NextAction {
    if (plan.canPublish) {
        return {
            ...base, kind: "publish", who: "staff", title: `Publish ${business}'s schedule`,
            why: `Baseline ${dayMonth(o.scan.finishedAt)}. Nothing is missing. ${business} sees no dates until it is published.`,
            cta: "Open the timeline", primary: true,
        } as NextAction;
    }
    const missing = plan.blockers ?? [];
    return {
        ...base, kind: "publish", who: "staff", title: `Finish ${business}'s plan to publish the schedule`,
        why: missing.length > 0
            ? `Not ready: ${listWords(missing)}. ${business} sees no dates until it is published.`
            : `The timeline lists what is missing. ${business} sees no dates until it is published.`,
        cta: "Open the timeline", primary: true,
    } as NextAction;
}

/** "a, b and c". */
function listWords(items: string[]): string {
    return items.length <= 1 ? (items[0] ?? "") : `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}

/** An Online client with the login out: Oura runs the first check the day brand checks are live. */
function waitBrand(base: Pick<NextAction, "waitingOn" | "row" | "primary" | "late" | "cta" | "secondary" | "tag">): NextAction {
    // Not "Oura is working": nothing runs until brand checks are live, so the card says Not yet, as its step does.
    return { ...base, kind: "wait_brand", who: "oura", tag: { label: "Not yet", tone: "gray" }, title: "The first AI check waits for brand checks", why: "Brand checks are not live yet. Oura runs the first check the day they are." };
}

/** The Businesses list needs one short line per client. */
export function onboardingSummary(o: Onboarding, now = Date.now(), opts: NextOptions = {}): { done: number; total: number; line: string; who: StepWho; late: boolean; complete: boolean; kind: NextKind; cta: string | null } {
    const { done, total } = onboardingProgress(o);
    const next = nextAction(o, now, opts);
    const open = o.scan.draft?.openDecisions ?? 0;
    const oneSetup = isOneSetup(o);
    const line: Record<NextKind, string> = {
        record_payment: "Waiting for payment",
        send_kickoff: oneSetup ? "Setup email not sent" : "Kick-off not sent",
        wait_form: oneSetup ? "Waiting for setup" : "Waiting for the form",
        nudge_form: oneSetup ? "Setup is late" : "Form is late",
        draft_building: "Drafting the 50",
        draft_failed: "The draft stopped",
        review_draft: toDecide(open),
        start_scan: "Ready to start",
        wait_scan: "AI check running",
        retry_scan: "AI check failed",
        release: "Ready to send",
        resend_login: "Login email failed",
        wait_login: "Login email sending",
        choose_signature: "Choose a question",
        confirm_access: "Confirm an invite",
        wait_brand: "Waiting for brand checks",
        wait_access: "Waiting for access",
        nudge_access: "Access is late",
        send_results: "Results and plan not sent",
        publish: "Schedule not published",
        done: "Complete",
    };
    return { done, total, line: line[next.kind], who: next.who, late: next.late, complete: next.kind === "done", kind: next.kind, cta: next.cta };
}

/** Where the merchant stands: what is held, what is theirs to do, what is ours to confirm. */
export function merchantProgress(m: MerchantOnboarding): { done: number; total: number; waiting: number; mine: number; next: AccessItem | null } {
    const done = m.access.filter(isHeld).length;
    const waiting = m.access.filter((a) => a.status === "claimed").length;
    const open = m.access.filter((a) => a.status === "not_started");
    return { done, total: m.access.length, waiting, mine: open.length, next: open[0] ?? null };
}

/* ------------------------------------------------------------------------- *
 * Form answers that match no client (#3977)
 * ------------------------------------------------------------------------- */

/** A client the answer can be filed on: being onboarded and still waiting for a form. */
export interface AssignTarget {
    businessId: string;
    name: string;
    domain: string;
}

/**
 * The clients an unmatched answer can be filed on: ONLY those still waiting
 * for a form.
 *
 * It used to offer every client being onboarded, with the answered ones sorted
 * last and labelled — on the thought that a first answer might itself have
 * been filed on the wrong client. That made a destructive path a dropdown
 * entry: filing onto an answered client replaces the answers it already has,
 * with no warning and nothing to undo it (Jack, #3991). Correcting a
 * misfiled answer is a different job from placing an unplaced one, and it is
 * not this one.
 *
 * Filtering here is not the whole guard — a screen left open keeps the old
 * list — so the server should refuse the same transition
 * (ourteam-backend#5329).
 */
export function assignTargets(onboardings: Record<string, Onboarding>): AssignTarget[] {
    return Object.values(onboardings)
        .filter((o) => o.form.answeredAt === null)
        .map((o) => ({
            businessId: o.businessId,
            name: o.businessName || o.domain,
            domain: o.domain,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
}

/** "1 form answer matches no client", "3 form answers match no client". */
export function unmatchedNoticeText(count: number): string {
    return `${count} form ${count === 1 ? "answer" : "answers"} ${count === 1 ? "matches" : "match"} no client`;
}

/*
 * A Next card press made elsewhere (the Overview card, a list row) for an
 * onboarding state that opens a drawer or a row (F14): it goes to Onboarding
 * with `?press=next`, and that page's own press runs once on arrival, so one
 * press does the same everywhere. The press rides in the URL, so it is
 * consumed by the page it opens and never lingers for a later visit.
 * Never a press that writes (the signature) or leaves the page.
 */
export const PRESS_PARAM = "press";
export const PRESS_NEXT = "next";

/** The kinds whose press only opens something on the Onboarding page. */
const OPENS_ON_ARRIVAL: ReadonlySet<NextKind> = new Set<NextKind>([
    "record_payment", "send_kickoff", "wait_form", "nudge_form", "wait_access", "nudge_access", "release", "resend_login", "confirm_access",
]);

/** True when a press carried over to the Onboarding page may run there by itself. */
export function opensOnArrival(kind: NextKind): boolean {
    return OPENS_ON_ARRIVAL.has(kind);
}
